import React, { useContext, useState } from "react";
import burger from "../images/burger.svg";
import { ReactComponent as RedirectIcon } from "../images/redirectIcon.svg";
import styled from "styled-components";
import { Label, FlexWrapper, CustomizedNavigationButton } from "./StyledComponents/StyledComponents";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import whiteRight from "../images/whiteRight.svg";
import darkRight from "../images/DarkRight.svg";
import cross from "../images/cross.svg";
import searchIcon from "../images/searchIcon.svg";
import { validURL, lightOrDark } from "../Constants/utility";
import { SearchContext, KBContext } from "../Context/index";
import MainLogo from "../images/defaultLogo.png";
import { customizeNavigationButton, FTR_KB_FOR_OUTLOOK, PRODUCT_KB_ORIGIN } from "../Constants/index";
import { isFeatureEnabled, isNewUIEnabled, composeAssetUrl } from "../utils";
import useNavigateWithSearch from "../Context/KBContext/hooks/useNavigateWithSearch";
import { HIVER_GMAIL, NAVIGATION_BUTTON_TEXT, HIVER_OMNI, OUTLOOK_PRODUCT_TYPE, OMNI_PRODUCT_TYPE } from "../Constants/KBContext.constants";
import { openExternalUrl } from "../Constants/security";

function getLogoSrc(bannerData, imageBaseUrl) {
  return composeAssetUrl(bannerData?.logo, imageBaseUrl) || MainLogo;
}

function getDropdownStyle(brandColor) {
  const isLight = lightOrDark(brandColor) === "light";
  return { color: isLight ? "#222222" : "#FFFFFF", arrowSrc: isLight ? whiteRight : darkRight };
}

function openUrl(url) {
  if (!validURL(url)) return;
  // Restrict to http(s) (blocks javascript:/data: schemes) and pass noopener so the
  // opened page cannot reach back via window.opener. Referrer is kept intentionally.
  openExternalUrl(url);
}

function getSwitchButtonConfig(product) {
  const isOutlookOrOmni = [OUTLOOK_PRODUCT_TYPE, OMNI_PRODUCT_TYPE].includes(product);
  return {
    text: NAVIGATION_BUTTON_TEXT.replace("{{product}}", isOutlookOrOmni ? HIVER_GMAIL : HIVER_OMNI),
    href: isOutlookOrOmni ? PRODUCT_KB_ORIGIN : `${PRODUCT_KB_ORIGIN}?product=${OMNI_PRODUCT_TYPE}`,
  };
}

/* Inside Layout Wrapper (padding from parent); no bottom margin so banner aligns flush below */
const HeaderWrapper = styled.header`
  box-sizing: border-box;
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 20px;
  margin: 0;
`;

const LogoWrapper = styled.img`
  max-width: 160px;
  max-height: 50px;
  cursor: pointer;
`;
const BurgerImage = styled.img`
  max-width: 20px;
  max-height: 20px;
  display: none;
  @media (max-width: 768px) {
    display: block;
  }
`;

const DropDownWrappper = styled.div`
  width: 100%;
  height: 75px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const RightImage = styled.img`
  height: 9.55px;
  width: 5.83px;
`;
const DropDownContent = styled.div`
  width: 85%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin: 0 auto;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 28px;
  letter-spacing: -0.138889px;
  cursor: pointer;
  color: ${(props) => props.color};
`;

function MobileDropdownItem({ label, onClick, brandColor }) {
  const { color, arrowSrc } = getDropdownStyle(brandColor);
  return (
    <DropDownWrappper onClick={onClick}>
      <DropDownContent color={color}>
        {label}
        <RightImage src={arrowSrc} />
      </DropDownContent>
    </DropDownWrappper>
  );
}

const DropDownContainer = styled.div`
  box-sizing: border-box;
  max-height: 150px;
  background-color: ${(props) => props.backColor};
`;

const Modal = styled.div`
  position: fixed; /* Stay in place */
  z-index: 1000; /* Sit on top */
  left: 0;
  top: 95px;
  width: 100%; /* Full width */
  height: 100%; /* Full height */
  //overflow: auto; /* Enable scroll if needed */
  background-color: rgb(0, 0, 0); /* Fallback color */
  background-color: rgba(0, 0, 0, 0.5); /* Black w/ opacity */

  @media screen and (max-width: 500px) {
    top: 85px;
  }

  @media (min-width: 769px) {
    display: none;
  }

  transition: top 1s;
`;

const HeaderNavigation = styled.div`
  margin-left: 40px;
`;
const BorderLine = styled.div`
  background: #313c4b;
  border-radius: 9px;
  height: 2px;
  margin-top: 2px;
`;

const GmailOutlookButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  align-items: center;
`;

const GmailOutlookButton = styled.button`
  display: ${({ hidden }) => hidden ? "none" : "flex"};
  align-items: center;
  gap: 6px;
  cursor: pointer;
  border: 1px solid #196fe0;
  border-radius: 4px;
  margin-right: 40px;
  padding: 0 18px;
  min-height: 36px;
  background-color: #ffffff;
  color: #196fe0;
  font-size: 14px;
  font-weight: 500px;
  line-height: 28px;
  font-family: Inter;
  svg {
    width: 16px;
    fill: #196fe0;
  }
  &:hover {
    color: #ffffff;
    background-color: #0456c2;
    border-color: #0456c2;
    svg {
      fill: #ffffff;
    }
  }
  @media (max-width: 768px) {
    display: none;
  }
`;

/* Same horizontal band as article content (ArticleWrapper margin) so search bar aligns with Home/TOC */
const ArticleHeaderOuter = styled.div`
  width: calc(100% - 360px);
  max-width: var(--kb-content-max-width, 1280px);
  margin: 0 180px;
  padding: 24px var(--kb-content-padding-x, 48px) 12px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 12px;
  @media (max-width: 1100px) {
    width: calc(100% - 180px);
    margin: 0 90px;
    padding: 24px var(--kb-content-padding-x, 40px) 12px;
  }
  @media (max-width: 800px) {
    width: calc(100% - 100px);
    margin: 0 50px;
  }
  @media (max-width: 500px) {
    width: calc(100% - 48px);
    margin: 0 24px;
    padding: 20px var(--kb-content-padding-x, 24px) 12px;
  }
  @media (max-width: 778px) {
    padding: 20px var(--kb-content-padding-x, 24px) 12px;
  }
`;

const ArticleLogoButtonRow = styled.div`
  width: 100%;
  min-height: 40px;
  padding: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const HeaderSearchWrapper = styled.div`
  flex: 1;
  max-width: 480px;
  margin: 0 24px;
  position: relative;
  display: flex;
  align-items: center;
  @media (max-width: 768px) {
    display: none;
  }
`;

const ArticleSearchRow = styled.div`
  width: 100%;
  min-width: 0;
  padding: 0;
  box-sizing: border-box;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  @media (max-width: 768px) {
    display: none;
  }
`;

const ArticleSearchFullWidth = styled.div`
  width: 100%;
  min-width: 0;
  position: relative;
  display: flex;
  align-items: center;
  box-sizing: border-box;
`;

/* Figma: search bar height 54px, border #E2E8F0, placeholder 18px #9AA0AE, icon 20px at 16px from left */
const HeaderSearchInput = styled.input`
  box-sizing: border-box;
  width: 100%;
  height: 54px;
  padding: 0 40px 0 46px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0;
  color: #1a202c;
  background-color: #ffffff;
  background-image: url(${searchIcon});
  background-repeat: no-repeat;
  background-position: 16px center;
  background-size: 20px 20px;
  outline: none;
  transition: box-shadow 0.2s ease;

  &::placeholder {
    color: #9aa0ae;
    font-size: 18px;
    font-weight: 400;
  }

  &:hover,
  &:focus,
  &:focus-visible {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04);
  }
`;

const HeaderSearchClear = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  display: ${(p) => (p.show ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  img {
    width: 16px;
    height: 16px;
  }
`;

const ArticlePageCtaButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 20px;
  min-height: 40px;
  border: none;
  border-radius: 4px;
  background: #f8d663;
  color: #000000;
  font-family: Inter, sans-serif;
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0;
  cursor: pointer;
  box-sizing: border-box;
  &:hover {
    background: #e6c04d;
  }
  @media (max-width: 768px) {
    display: none;
  }
`;

/* Figma: simple header — logo left, CTA right, items-center, justify-between */
const FigmaHeaderWrapper = styled.header`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 20px;
  margin: 0;
`;

function isInnerPageHeader(pathname) {
  const segments = pathname.replace(/^\//, "").split("/").filter(Boolean);
  return segments.length >= 1;
}

export default function Header({ bannerData }) {
  const { setSearchInput, setSearchList, searchInput, triggerSearch } = useContext(SearchContext);
  const { search, pathname } = useLocation();
  const navigate = useNavigateWithSearch();
  const [searchParams] = useSearchParams();
  const Image_Base_Url = process.env.REACT_APP_IMAGE_BASE_URL;
  const [openDropDown, setOpenDropDown] = useState(false);
  const { availableFeatures } = useContext(KBContext);
  const showCustomizedNavigationButton = isFeatureEnabled(customizeNavigationButton, availableFeatures);
  const showSwitchKbButton = isFeatureEnabled(FTR_KB_FOR_OUTLOOK, availableFeatures);
  const handleBurgerClick = () => {
    setOpenDropDown(!openDropDown);
  };
  const product = searchParams.get("product");
  const isNewUI = isNewUIEnabled(availableFeatures);
  const { text: buttonText, href: buttonHref } = getSwitchButtonConfig(product);
  const showButtonForOrigin = showSwitchKbButton && window.origin === PRODUCT_KB_ORIGIN;
  const showButtonForUg = showSwitchKbButton && isNewUI;
  const showButton = showButtonForOrigin || showButtonForUg;

  const handleNavigationClicked = (url) => openUrl(url);
  const navigateToAltKB = () => window.open(buttonHref, "_blank");
  const handleNavCtaClick = () => handleNavigationClicked(bannerData?.navigationButtonUrl);

  const handleLogoClick = () => {
    setSearchInput("");
    setSearchList([]);
    navigate("/");
  };

  const showNavLinks = true;
  const isSubmitIssuePage = pathname === "/submit-issue";
  const showHelpLink = bannerData?.enableHelpForm;
  const showNavButton = !!Object.keys(availableFeatures).length && bannerData?.navigationButtonText;
  const showBurger = (showHelpLink || !!bannerData?.navigationButtonText) && showNavLinks;


  const logoSrc = getLogoSrc(bannerData, Image_Base_Url);

  const isInnerPage = isInnerPageHeader(pathname);

  /* ─── Inner pages: logo + switch-KB button (when help.hiverhq.com) + CTA ─── */
  if (isInnerPage) {
    return (
      <FigmaHeaderWrapper>
        <GmailOutlookButtonWrapper>
          <LogoWrapper onClick={handleLogoClick} src={logoSrc} />
          <GmailOutlookButton hidden={!showButton} onClick={navigateToAltKB}>
            {buttonText}
            <RedirectIcon />
          </GmailOutlookButton>
        </GmailOutlookButtonWrapper>
        {bannerData?.navigationButtonText && (
          <CustomizedNavigationButton bannerData={bannerData} onClick={handleNavCtaClick}>
            {bannerData.navigationButtonText}
          </CustomizedNavigationButton>
        )}
      </FigmaHeaderWrapper>
    );
  }

  const headerContent = (
    <HeaderWrapper>
      <GmailOutlookButtonWrapper>
        <LogoWrapper onClick={handleLogoClick} src={logoSrc} />
        <GmailOutlookButton hidden={!showButton} onClick={navigateToAltKB}>
          {buttonText}
          <RedirectIcon />
        </GmailOutlookButton>
      </GmailOutlookButtonWrapper>

      {showNavLinks && (
        <FlexWrapper width="auto" hide align="center" justify="center">
          {showHelpLink && (
            <HeaderNavigation>
              <Link to={"/submit-issue" + search} style={{ textDecoration: "none" }}>
                <Label className={isSubmitIssuePage ? "headerLabelSelect" : "headerLabel"}>
                  {bannerData?.helpPageName}
                </Label>
              </Link>
              {isSubmitIssuePage && <BorderLine />}
            </HeaderNavigation>
          )}
          {showNavButton && (
            <HeaderNavigation>
              {showCustomizedNavigationButton ? (
                <CustomizedNavigationButton bannerData={bannerData} onClick={handleNavCtaClick}>
                  {bannerData.navigationButtonText}
                </CustomizedNavigationButton>
              ) : (
                <Label onClick={handleNavCtaClick} className="headerLabel">
                  {bannerData.navigationButtonText}
                </Label>
              )}
            </HeaderNavigation>
          )}
        </FlexWrapper>
      )}

      {showBurger && (
        <BurgerImage onClick={handleBurgerClick} src={openDropDown ? cross : burger} />
      )}
      </HeaderWrapper>
  );

  return (
    <>
      {headerContent}
      {openDropDown && (
        <Modal>
          <DropDownContainer backColor={bannerData?.brandColor || "rgba(20, 115, 230, 1)"}>
            {bannerData?.enableHelpForm && (
              <MobileDropdownItem
                label="Submit an Issue"
                onClick={() => navigate("/submit-issue")}
                brandColor={bannerData?.brandColor}
              />
            )}
            {bannerData?.navigationButtonText && (
              <MobileDropdownItem
                label={bannerData.navigationButtonText}
                onClick={handleNavCtaClick}
                brandColor={bannerData?.brandColor}
              />
            )}
          </DropDownContainer>
        </Modal>
      )}
    </>
  );
}
